import { useEffect, useRef } from 'react'

// Elements that can receive keyboard focus
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  "input:not([disabled]):not([type='hidden'])",
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(', ')

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    // Exclude elements that are hidden (e.g., display: none, visibility: hidden, etc.)
    (el) => el.offsetParent !== null || el === document.activeElement
  )
}

/**
 * Hook to trap focus within a container when `isActive = true`.
 *
 * - Tab / Shift+Tab cycles through focusable elements inside the container only
 * - Automatically focuses the first focusable element when activated
 * - Restores focus to the element that opened the modal when deactivated (returnFocus)
 *
 * Usage:
 *   const modalRef = useFocusTrap(isOpen);
 *   <div ref={modalRef} role="dialog" aria-modal="true">...</div>
 */

export function useFocusTrap({ isActive, autoFocus = true, focusLock = true, returnFocus = true }) {
  const containerRef = useRef(null)
  const previouslyFocusedRef = useRef(null)

  useEffect(() => {
    if (!isActive) return

    const container = containerRef.current
    if (!container) return

    // 1. Store the currently focused element before opening the modal
    if (returnFocus) {
      previouslyFocusedRef.current = document.activeElement
    }

    container.setAttribute('tabindex', '-1')
    container.focus()

    // 2. Move focus into the modal when it opens
    if (autoFocus) {
      const focusables = getFocusableElements(container)
      if (focusables.length > 0) focusables[0].focus()
    }

    if (!focusLock) {
      return () => {
        if (returnFocus) previouslyFocusedRef.current?.focus?.()
      }
    }

    // 3. Prevent Tab navigation from leaving the modal
    function handleKeyDown(e) {
      if (e.key !== 'Tab') return

      const items = getFocusableElements(container)

      if (items.length === 0) {
        e.preventDefault()
        return
      }

      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement

      if (e.shiftKey) {
        // Shift+Tab on the first element → move focus to the last element
        if (active === first || !container.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else {
        // Tab on the last element → move focus to the first element
        if (active === last || !container.contains(active)) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)

      // 4. Restore focus to the element that opened the modal
      if (returnFocus) previouslyFocusedRef.current?.focus?.()
    }
  }, [isActive, autoFocus, focusLock, returnFocus])

  return containerRef
}
